import { useContentLink, usePageContent, useData } from "@robe/client";
import Head from "next/head";
import { Fragment, PropsWithChildren, useEffect, useState } from "react";
import { useLayout } from "xcore";
import { FileInfo, LocalesValue, sentryLogger } from "@robe/core";
import { useRouter } from "next/router";
import { isNewsGoogleStructuredDataType, NewsGoogleStructuredDataType } from "utils/googleStructuredData/useNewsGoogleStructuredData";
import {
  isProductGoogleStructuredDataType,
  ProductGoogleStructuredDataType,
} from "utils/googleStructuredData/useProductGoogleStructuredData";

interface RobeHeadProps {
  title?: string | string[];
  metaTitle?: string | string[];
  description?: string;
  url?: string;
  img?: string | FileInfo | null;
  googleStructuredData?: NewsGoogleStructuredDataType | ProductGoogleStructuredDataType | null;
}

/**
 * Content title is used for page meta title if no other is specified.
 * Some contents have hardcoded titles, that are usually used only in links
 * and titles in values that can be used as page title.
 */
const RobeHead = ({
  title: titleSpecified,
  metaTitle,
  googleStructuredData,
  description: _description,
  url,
  img,
  children,
}: PropsWithChildren<RobeHeadProps>) => {
  const { locale } = useRouter();
  const { general, contentTypes, config, stringify, image, resolvedDomains } = useLayout();
  const { pageContentPublishedLocales } = useData();
  const content = usePageContent();
  const { as } = useContentLink();

  const [currentDomain, setCurrentDomain] = useState<null | string>(null);
  const [resolvedPathname, setResolvedPathname] = useState<null | string>(null);
  const [pathHasQueryParamsOrAnchor, setPathHasQueryParamsOrAnchor] = useState(false);

  const title = titleSpecified ?? stringify(content.title);
  const titleResolved = resolveTitle(title);
  const metaTitleResolved = resolveTitle(metaTitle ?? title);
  const description = _description ?? stringify(general.values?.metaDescription);

  const contentUrl = config.env === "local" ? null : (url ?? as(content, { locale }));

  useEffect(() => {
    setCurrentDomain(window.location.origin);
    setResolvedPathname(window.location.pathname);

    (window.location.search !== "" || window.location.hash !== "") && setPathHasQueryParamsOrAnchor(true);
  }, []);

  const validateGoogleStructuredData = () =>
    googleStructuredData?.["@type"] === "Product"
      ? isProductGoogleStructuredDataType(googleStructuredData)
      : googleStructuredData?.["@type"] === "Article"
        ? isNewsGoogleStructuredDataType(googleStructuredData)
        : false;

  const isValidGoogleStructuredData = validateGoogleStructuredData();

  const stringifyGoogleStructuredData = (): string | null => {
    try {
      return JSON.stringify(googleStructuredData);
    } catch (error) {
      sentryLogger({
        error,
        message: "Unexpected error occurred while serializing Google Structured Data",
        context: {
          googleStructuredData,
        },
      });

      return null;
    }
  };

  const stringifiedGoogleStructuredData = stringifyGoogleStructuredData();
  const localesAlternateDomains = resolveAlternateDomains(config.locales);

  return (
    <Head>
      <title key="title">{titleResolved}</title>

      {/* Add google structured data only if the structure is valid and if the stringify function does not throw an error in order to make dangerouslySetInnerHTML as safe as possible */}
      {/* It is not possible to pass JSON.stringify(googleStructuredData) directly to the script because the text gets encoded and Google Search Console does not recognize it as valid Google Structured Data. DangerouslySetInnerHTML required to bypass the encoding */}
      {stringifiedGoogleStructuredData && isValidGoogleStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: stringifiedGoogleStructuredData,
          }}
        />
      )}

      <meta key="og_title" property="og:title" content={metaTitleResolved} />
      <meta key="meta_title" name="title" content={metaTitleResolved} />

      <meta key="og_description" property="og:description" content={description} />
      <meta key="description" name="description" content={description} />

      {contentUrl && contentTypes[content.type]?.routes?.[locale ?? "en"] && <meta key="og_url" property="og:url" content={contentUrl} />}

      <meta
        key="og_image"
        property="og:image"
        content={(typeof img === "string" ? img : img ? image(img) : null) ?? "/static/og-image@2x.png"}
      />

      <meta key="og_locale" property="og:locale" content={locale} />

      {config.env !== "local" &&
        resolvedDomains &&
        localesAlternateDomains.map(({ locale: l, regionLocale, domain }, i) => {
          const localizedContentUrl = as(content, { locale: l });

          // Render links to alternate locales domains if content is published for these locales
          if (pageContentPublishedLocales.includes(l) && localizedContentUrl) {
            const localizedContentUrlObj = new URL(localizedContentUrl);
            // Replace main locale domain with region specific one
            localizedContentUrlObj.hostname = domain;

            return <link key={i} rel="alternate" href={localizedContentUrlObj.toString()} hrefLang={regionLocale ?? l}></link>;
          }

          return <Fragment key={i} />;
        })}

      {/* Prevent indexing url with query params or anchor */}
      {pathHasQueryParamsOrAnchor && <meta name="robots" content="noindex" />}
      {/* Point search engines to the main version of the content */}
      {currentDomain && resolvedPathname && <link rel="canonical" href={`${currentDomain}${resolvedPathname}`} />}

      {children}
    </Head>
  );
};

export default RobeHead;

const resolveTitle = (title: string | string[]) => {
  return typeof title === "string" ? title : title.filter(Boolean).join(" | ");
};

const resolveAlternateDomains = (locales: Record<string, LocalesValue>) => {
  const alternateDomainsArray: {
    domain: string;
    locale: string;
    regionLocale?: string;
  }[] = [];

  Object.entries(locales).forEach(([l, { routing, domains }]) => {
    if (domains && domains.length > 0) {
      domains.forEach(({ domain, regionsLocales }) => {
        if (regionsLocales && regionsLocales.length > 0) {
          regionsLocales.forEach((regionLocale) =>
            alternateDomainsArray.push({
              domain,
              locale: l,
              regionLocale,
            })
          );
        } else {
          alternateDomainsArray.push({
            domain,
            locale: l,
            regionLocale: routing,
          });
        }
      });
    }
  });

  return alternateDomainsArray;
};
